/**
 * Travel Assistant Repository Implementation
 *
 * Single Responsibility: Handles all travel assistant-related data operations
 * Dependency Inversion: Depends on IHttpClient abstraction
 * Open/Closed: Can be extended without modifying existing methods
 */

import { IHttpClient } from '../interfaces/IHttpClient';
import {
  ITravelAssistantRepository,
  OverviewRequest,
  OverviewResponse,
  QuickPeekRequest,
  QuickPeekResponse,
} from '../interfaces/ITravelAssistantRepository';
import {
  TravelAssistantTransformer,
  ApiOverviewResponse,
} from '../transformers/TravelAssistantTransformer';
import axios, { AxiosError } from 'axios';

export class TravelAssistantRepository implements ITravelAssistantRepository {
  private httpClient: IHttpClient;
  private baseUrl: string;

  constructor(httpClient: IHttpClient, baseUrl: string) {
    this.httpClient = httpClient;
    this.baseUrl = baseUrl;
  }

  async getOverview(
    request: OverviewRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<OverviewResponse> {
    // Use Next.js API route to avoid CORS issues and ensure correct API endpoint
    const url = '/api/travel-assistant/overview';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<ApiOverviewResponse>(
        url,
        request,
        { headers, signal }
      );

      const transformed = TravelAssistantTransformer.transformOverview(
        response.data
      );

      return transformed;
    } catch (error) {
      // handleError throws a properly formatted error, so we don't need to re-throw
      this.handleError('getOverview', error);
    }
  }

  async getQuickPeekTravelPlanning(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/travel-planning';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekTravelPlanning', error);
    }
  }

  async getQuickPeekTravelPlanningOnly(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/travel-planning-only';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekTravelPlanningOnly', error);
    }
  }

  async getQuickPeekNavigationAccessibility(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/navigation-accessibility';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekNavigationAccessibility', error);
    }
  }

  async getQuickPeekCulturalInfo(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/cultural-info';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekCulturalInfo', error);
    }
  }

  async getQuickPeekCulturalSocial(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/cultural-social';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekCulturalSocial', error);
    }
  }

  async getQuickPeekHistoricalSouvenirs(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/historical-souvenirs';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekHistoricalSouvenirs', error);
    }
  }

  async getQuickPeekActivitiesFacilities(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse>> {
    const url = '/api/travel-assistant/quick-peek/activities-facilities';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: Partial<QuickPeekResponse>;
          [key: string]: unknown;
        };
        response?: Partial<QuickPeekResponse>;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      this.handleError('getQuickPeekActivitiesFacilities', error);
    }
  }

  async getQuickPeek(
    request: QuickPeekRequest,
    accessToken?: string,
    refreshToken?: string,
    signal?: AbortSignal
  ): Promise<QuickPeekResponse> {
    // Use Next.js API route to avoid CORS issues
    const url = '/api/travel-assistant/quick-peek';
    const headers = TravelAssistantTransformer.buildAuthHeaders(
      accessToken,
      refreshToken
    );

    try {
      const response = await this.httpClient.post<{
        code?: number;
        success?: boolean;
        data?: {
          response?: QuickPeekResponse;
          [key: string]: unknown;
        };
        response?: QuickPeekResponse;
        [key: string]: unknown;
      }>(url, request, { headers, signal });

      // Type assertion needed because transformer accepts flexible API response structure
      return TravelAssistantTransformer.transformQuickPeek(
        response.data as Record<string, unknown>
      );
    } catch (error) {
      // handleError throws a properly formatted error, so we don't need to re-throw
      this.handleError('getQuickPeek', error);
    }
  }

  private handleError(operation: string, error: unknown): void {
    // Check if it's an AxiosError
    if (axios.isCancel(error)) {
      // Don't throw for canceled requests - just return silently
      return;
    }
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      const statusText = axiosError.response?.statusText;
      const errorMessage = axiosError.response?.data as {
        message?: string;
      };

      // Build error message with proper fallbacks
      let fullErrorMessage = `Failed to ${operation}`;
      if (status) {
        fullErrorMessage += `: ${status}`;
        if (statusText) {
          fullErrorMessage += ` ${statusText}`;
        }
      } else if (axiosError.message) {
        fullErrorMessage += `: ${axiosError.message}`;
      } else {
        fullErrorMessage += ': Unknown error';
      }

      if (errorMessage?.message) {
        fullErrorMessage += ` - ${errorMessage.message}`;
      }

      console.error('API Error:', {
        operation,
        status,
        statusText,
        message: errorMessage?.message || axiosError.message,
        url: axiosError.config?.url,
        code: axiosError.code,
      });

      throw new Error(fullErrorMessage);
    }

    // Handle regular Error instances
    if (error instanceof Error) {
      console.error(`Error in ${operation}:`, {
        message: error.message,
        stack: error.stack,
      });
      throw new Error(`Failed to ${operation}: ${error.message}`);
    }

    // Handle unknown error types
    console.error(`Unknown error in ${operation}:`, error);
    throw new Error(`Failed to ${operation}: Unknown error occurred`);
  }
}
